import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { getCart, addToCart as apiAddToCart, removeFromCart as apiRemoveFromCart } from '../utils/cart';

const CartContext = createContext({
  items: [],
  count: 0,
  totalCost: null,
  loading: false,
  addToCart: async () => {},
  removeFromCart: async () => {},
  refreshCart: () => {},
});

export function CartProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshCart = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const data = await getCart();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const addToCart = useCallback(
    async (poojaOrIds) => {
      if (!isAuthenticated) return { success: false };
      try {
        const result = await apiAddToCart(poojaOrIds);
        await refreshCart();
        return result;
      } catch (err) {
        throw err;
      }
    },
    [isAuthenticated, refreshCart]
  );

  const removeFromCart = useCallback(
    async (cartItemId) => {
      if (!isAuthenticated) return;
      await apiRemoveFromCart(cartItemId);
      await refreshCart();
    },
    [isAuthenticated, refreshCart]
  );

  const count = items.length;
  const totalCost = useMemo(() => {
    let sum = 0;
    let hasPrice = false;
    for (const item of items) {
      const fees = item?.participationFees || item?.participation_fees || [];
      const amounts = [];
      for (const fee of fees) {
        const n = Number(fee?.amount);
        if (!Number.isNaN(n)) amounts.push(n);
        for (const variant of fee?.variants || []) {
          const vn = Number(variant?.amount);
          if (!Number.isNaN(vn)) amounts.push(vn);
        }
      }
      const price = amounts.length ? Math.min(...amounts) : null;
      if (price != null && !Number.isNaN(price)) {
        sum += price;
        hasPrice = true;
      }
    }
    return hasPrice ? sum : null;
  }, [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        count,
        totalCost,
        loading,
        addToCart,
        removeFromCart,
        refreshCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
